/**
 * NT-JOBS-BETRIEBE-SHORTCUT-01: reine, DOM-freie Logik für die angepinnte "BETRIEBE"-Sektion
 * oberhalb der Jobs-Liste — ausgelagert aus dashboard.ts (Muster crimeOps.categories.ts), damit
 * sie ohne den restlichen, seiteneffektreichen Dashboard-Code isoliert testbar ist.
 */

/** Minimaler Ausschnitt aus JobStatusItem (dashboard.ts), den diese Logik tatsächlich braucht. */
export interface BetriebeShortcutJob {
  id: string;
  name: string;
  playerState: {
    isUnlocked: boolean;
    isHidden: boolean;
    missingTalentNames: string[];
  };
}

export interface BetriebeShortcutEntryDef {
  jobId: string;
  dataAttr: string;
}

export interface BetriebeShortcutEntry<T extends BetriebeShortcutJob = BetriebeShortcutJob> {
  job: T;
  dataAttr: string;
}

/**
 * Fest verdrahtete Reihenfolge nach Freischaltprogression (flaschensammler unlockLevel 2 <
 * pfandflaschen_imperium ab Sammler-Job-Level 7 < werkstatt ab Sammler-Job-Level 10) —
 * deterministisch, unabhängig von der Payload-Reihenfolge aus `/jobs/status`. `dataAttr` ist
 * exakt das Attribut, das der jeweilige job-card-eigene VERWALTEN/ABGEBEN-Button trägt
 * (dashboard.ts::renderPfandRow/renderBetriebRow/renderWerkstattRow) — der bereits bestehende,
 * global delegierte Click-Handler (dashboard.ts::handleClick()) bedient es unverändert, kein
 * neuer Handler nötig.
 */
export const BETRIEBE_SHORTCUT_ENTRIES: readonly BetriebeShortcutEntryDef[] = [
  { jobId: 'flaschensammler', dataAttr: 'data-pfand-open' },
  { jobId: 'pfandflaschen_imperium', dataAttr: 'data-pfand-betrieb-open' },
  { jobId: 'werkstatt', dataAttr: 'data-werkstatt-open' },
];

/**
 * Deckt sich exakt mit der Bedingung, unter der renderPfandRow/renderBetriebRow/renderWerkstattRow
 * auf der jeweiligen Job-Karte selbst eine funktionierende VERWALTEN/ABGEBEN-Zeile zeigen (ACTIVE-,
 * COOLDOWN- und VERFÜGBAR-Zustand, nicht in den beiden GESPERRT-Zuständen) — reine Ableitung aus
 * bereits geladenen Feldern, kein neuer Freischaltzustand.
 */
export function isBetriebeShortcutVisible(job: BetriebeShortcutJob): boolean {
  const ps = job.playerState;
  return ps.isUnlocked && !ps.isHidden && ps.missingTalentNames.length === 0;
}

/**
 * Filtert + ordnet die sichtbaren Einträge aus einer beliebig sortierten allJobs-Liste. Ohne
 * mindestens ein freigeschaltetes Nebengeschäft ist das Ergebnis ein leeres Array — der Aufrufer
 * lässt die Sektion dann komplett weg (kein leerer Kasten, kein Platzhalter).
 */
export function selectVisibleBetriebeShortcuts<T extends BetriebeShortcutJob>(
  allJobs: readonly T[],
): BetriebeShortcutEntry<T>[] {
  const entries: BetriebeShortcutEntry<T>[] = [];
  for (const { jobId, dataAttr } of BETRIEBE_SHORTCUT_ENTRIES) {
    const job = allJobs.find((j) => j.id === jobId);
    if (job && isBetriebeShortcutVisible(job)) entries.push({ job, dataAttr });
  }
  return entries;
}
